import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Slider,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  Button,
  useTheme,
  Divider,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { CityService } from '../../services/city/CityService';
import { RegionService } from '../../services/region/RegionService';
import { DistrictService } from '../../services/district/DistrictService';
import { EventService } from '../../services/event/EventService';
import { EstabilishmentService } from '../../services/estabilishment/EstabilishmentService';
import MapIcon from '@mui/icons-material/Map';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import TuneIcon from '@mui/icons-material/Tune';

interface FilterFormProps {
  open: boolean;
  toggleFilter: any;
  searchParams: any;
  setSearchParams: any;
  onViewResults?: () => void;
}

const FilterForm: React.FC<FilterFormProps> = ({
  open,
  toggleFilter,
  searchParams,
  setSearchParams,
  onViewResults,
}) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const initialCidade = searchParams.get('cidade') || '';
  const initialRegiao = searchParams.get('regiao') || '';
  const initialBairro = searchParams.get('bairro') || '';
  const initialEvento = searchParams.get('evento') || '';
  const initialEspaco = searchParams.get('espaco') || '';
  const initialConvidados = Number(searchParams.get('convidados') || 20);
  const initialDataHoraParam = searchParams.get('dataHora');
  const parsedInitialDataHora = initialDataHoraParam ? dayjs(initialDataHoraParam) : dayjs();
  const initialDataHora = parsedInitialDataHora.isValid() ? parsedInitialDataHora : dayjs();

  const [cities, setCities] = React.useState([]);
  const [regions, setRegions] = React.useState([]);
  const [districts, setDistricts] = React.useState([]);
  const [events, setEvents] = React.useState([]);
  const [estabilishments, setEstabilishments] = React.useState([]);

  const [cidade, setCidade] = React.useState(initialCidade);
  const [regiao, setRegiao] = React.useState(initialRegiao);
  const [bairro, setBairro] = React.useState(initialBairro);
  const [evento, setEvento] = React.useState(initialEvento);
  const [espaco, setEspaco] = React.useState(initialEspaco);
  const [convidados, setConvidados] = React.useState<number>(initialConvidados);
  const [dataHora, setDataHora] = React.useState<dayjs.Dayjs | null>(initialDataHora);
  const [caracteristicas, setCaracteristicas] = React.useState({
    ar: searchParams.get('ar') === 'true',
    buffet: searchParams.get('buffet') === 'true',
    estacionamento: searchParams.get('estacionamento') === 'true',
    aoArLivre: searchParams.get('aoArLivre') === 'true',
    som: searchParams.get('som') === 'true',
    salas: searchParams.get('salas') === 'true',
  });

  React.useEffect(() => {
    CityService.search().then((response) => {
      setCities(response.data);
    });

    EventService.search().then((response) => {
      setEvents(response.data);
    });

    EstabilishmentService.search().then((response) => {
      setEstabilishments(response.data);
    });

    // Carregar regiões e distritos se já houver cidade/região na URL
    const cidadeParam = searchParams.get('cidade');
    const regiaoParam = searchParams.get('regiao');

    if (cidadeParam) {
      RegionService.searchByCity(cidadeParam).then((response) => {
        setRegions(response.data);
      });

      if (regiaoParam || cidadeParam) {
        DistrictService.search(regiaoParam || '', cidadeParam).then((response) => {
          setDistricts(response.data);
        });
      }
    }
  }, []);

  React.useEffect(() => {
    const dataHoraParam = searchParams.get('dataHora');
    if (dataHoraParam) {
      const parsedDataHora = dayjs(dataHoraParam);
      setDataHora(parsedDataHora.isValid() ? parsedDataHora : dayjs());
    } else {
      setDataHora(dayjs());
    }

    const convidadosParam = searchParams.get('convidados');
    if (convidadosParam !== null) {
      const parsedValue = Number(convidadosParam);
      setConvidados(Number.isFinite(parsedValue) ? parsedValue : 20);
    } else {
      setConvidados(20);
    }

    const eventoParam = searchParams.get('evento');
    setEvento(eventoParam || '');

    setRegions([]);
    setRegiao(initialRegiao || '');
    setDistricts([]);
    setBairro(initialBairro || '');

    if (cidade) {
      RegionService.searchByCity(cidade).then((response) => {
        setRegions(response.data);
      });
    }
  }, [cidade, searchParams]);

  React.useEffect(() => {
    setDistricts([]);
    setBairro(initialBairro || '');

    if (!!regiao || !!cidade) {
      DistrictService.search(regiao, cidade).then((response) => {
        setDistricts(response.data);
      });
    }
  }, [regiao, cidade]);

  React.useEffect(() => {
    const params: any = {
      cidade,
      regiao,
      bairro,
      evento,
      espaco,
      convidados: convidados,
      dataHora: dataHora && dataHora.isValid() ? dataHora.toISOString() : dayjs().toISOString(),
      ...Object.entries(caracteristicas).reduce((acc, [key, value]) => {
        if (value) acc[key] = 'true';
        return acc;
      }, {} as Record<string, string>),
    };

    setSearchParams(params);
  }, [cidade, regiao, bairro, evento, espaco, convidados, dataHora, caracteristicas, searchParams]);

  const handleChangeCaracteristica =
    (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setCaracteristicas({
        ...caracteristicas,
        [name]: event.target.checked,
      });
    };

  const handleResetFilters = () => {
    searchParams.set('bairro', '');
    searchParams.set('regiao', '');
    searchParams.set('cidade', '');
    searchParams.set('evento', '');
    searchParams.set('espaco', '');
    searchParams.set('convidados', '20');
    searchParams.set('dataHora', dayjs().toISOString());

    setCidade('');
    setRegiao('');
    setBairro('');
    setRegions([]);
    setDistricts([]);
    setEvento('');
    setEspaco('');
    setConvidados(20);
    setDataHora(dayjs());
    setCaracteristicas({
      ar: false,
      buffet: false,
      estacionamento: false,
      aoArLivre: false,
      som: false,
      salas: false,
    });
  };

  const handleViewResults = () => {
    if (onViewResults) {
      onViewResults();
    }
  };

  // Section Header Component
  const SectionHeader = ({ icon: Icon, title }: { icon: any; title: string }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: isSmallMobile ? 1 : 1.5,
        mb: isSmallMobile ? 1.25 : 2,
      }}
    >
      <Icon sx={{ color: 'primary.main', fontSize: isSmallMobile ? 18 : 24 }} />
      <Typography fontWeight="bold" color="primary" variant={isSmallMobile ? 'body2' : 'subtitle1'}>
        {title}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? 1 : 2 }}>
      {/* Location Section */}
      <Box>
        <SectionHeader icon={MapIcon} title="Localização" />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? 1 : 1.5 }}>
          <Autocomplete
            size="small"
            options={cities}
            value={cities.find((city: any) => String(city?.id) === String(cidade)) || null}
            onChange={(e, newValue: any) => setCidade(newValue?.id || '')}
            getOptionLabel={(city: any) => city?.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
              <TextField {...params} label="Cidade" size="small" fullWidth />
            )}
          />

          {regions.length > 0 && (
            <Autocomplete
              size="small"
              options={regions}
              value={regions.find((region: any) => String(region?.id) === String(regiao)) || null}
              onChange={(e, newValue: any) => setRegiao(newValue?.id || '')}
              getOptionLabel={(region: any) => region?.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField {...params} label="Região" size="small" fullWidth />
              )}
            />
          )}

          {districts.length > 0 && (
            <Autocomplete
              size="small"
              options={districts}
              value={
                districts.find((district: any) => String(district?.id) === String(bairro)) || null
              }
              onChange={(e, newValue: any) => setBairro(newValue?.id || '')}
              getOptionLabel={(district: any) => district?.name || ''}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField {...params} label="Bairro" size="small" fullWidth />
              )}
            />
          )}
        </Box>
      </Box>

      <Divider />

      {/* Event Details Section */}
      <Box>
        <SectionHeader icon={EventIcon} title="Evento" />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Data do Evento"
              value={dataHora}
              onChange={(newValue) => setDataHora(newValue)}
              format="DD/MM/YYYY"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
            />
          </LocalizationProvider>

          <Autocomplete
            size="small"
            options={events}
            value={events.find((event: any) => String(event?.id) === String(evento)) || null}
            onChange={(e, newValue: any) => setEvento(newValue?.id || '')}
            getOptionLabel={(event: any) => event?.name || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField {...params} label="Tipo de Evento" size="small" fullWidth />
            )}
          />

          <Autocomplete
            size="small"
            options={estabilishments}
            value={
              estabilishments.find(
                (estabilishment: any) => String(estabilishment?.id) === String(espaco),
              ) || null
            }
            onChange={(e, newValue: any) => setEspaco(newValue?.id || '')}
            getOptionLabel={(estabilishment: any) => estabilishment?.name || ''}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            renderInput={(params) => (
              <TextField {...params} label="Tipo de Espaço" size="small" fullWidth />
            )}
          />
        </Box>
      </Box>

      <Divider />

      {/* Capacity Section */}
      <Box>
        <SectionHeader icon={PeopleIcon} title={`Convidados (${convidados} pessoas)`} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? 1 : 1.5 }}>
          <TextField
            type="number"
            label="Número de Convidados"
            size="small"
            fullWidth
            value={convidados}
            onChange={(e) => setConvidados(Number(e.target.value))}
            inputProps={{ min: 0, max: 500 }}
          />
          <Slider
            value={convidados}
            onChange={(e, newVal) => setConvidados(newVal as number)}
            valueLabelDisplay="auto"
            min={0}
            max={500}
            marks={[
              { value: 0, label: '0' },
              { value: 250, label: '250' },
              { value: 500, label: '500+' },
            ]}
          />
        </Box>
      </Box>

      <Divider />

      {/* Features Section */}
      <Box>
        <SectionHeader icon={TuneIcon} title="Comodidades" />
        <FormGroup
          sx={{ display: 'flex', flexDirection: 'column', gap: isSmallMobile ? 0.25 : 0.5 }}
        >
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={caracteristicas.ar}
                onChange={handleChangeCaracteristica('ar')}
              />
            }
            label={
              <Typography variant={isSmallMobile ? 'caption' : 'body2'}>Ar Condicionado</Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={caracteristicas.buffet}
                onChange={handleChangeCaracteristica('buffet')}
              />
            }
            label={
              <Typography variant={isSmallMobile ? 'caption' : 'body2'}>Buffet Próprio</Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={caracteristicas.estacionamento}
                onChange={handleChangeCaracteristica('estacionamento')}
              />
            }
            label={
              <Typography variant={isSmallMobile ? 'caption' : 'body2'}>Estacionamento</Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={caracteristicas.aoArLivre}
                onChange={handleChangeCaracteristica('aoArLivre')}
              />
            }
            label={
              <Typography variant={isSmallMobile ? 'caption' : 'body2'}>
                Espaço ao Ar Livre
              </Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={caracteristicas.som}
                onChange={handleChangeCaracteristica('som')}
              />
            }
            label={
              <Typography variant={isSmallMobile ? 'caption' : 'body2'}>Sonorização</Typography>
            }
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={caracteristicas.salas}
                onChange={handleChangeCaracteristica('salas')}
              />
            }
            label={
              <Typography variant={isSmallMobile ? 'caption' : 'body2'}>Múltiplas Salas</Typography>
            }
          />
        </FormGroup>
      </Box>

      {/* Mobile Footer */}
      <Box sx={{ display: 'flex', gap: 1, pt: 1, mb: 5 }}>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          onClick={handleResetFilters}
          size="small"
        >
          Redefinir
        </Button>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleViewResults}
          size="small"
        >
          Ver Resultados
        </Button>
      </Box>
    </Box>
  );
};

export default FilterForm;
